/**
 * `/main` child routes a restaurant_admin can reach once their restaurant is
 * `published` but not yet fully `activated`. A published-but-not-activated
 * restaurant can manage its public-facing profile/menu and account settings,
 * but the rest of the app (orders, tables, staff, billing, ...) stays out of
 * reach until fully activated.
 */
export const PUBLISHED_STATUS_ROUTES: string[] = [
  'dashboard',
  'menu',
  'add-menu',
  'update-menu',
  'type-and-categories',
  'customization',
  'restaurant-profile',
  'hardware',
  'billing-and-subscription',
  'profile',
  'appearance',
  'change-password',
  'notifications',
];

/**
 * Restaurant statuses that may sign in to the dashboard before full
 * activation. `created` (set up, not yet published) gets the same limited
 * access as `published`: the routes, sidebar items and quick-add rules below.
 */
export const LIMITED_ACCESS_STATUSES: string[] = ['created', 'published'];

/** True for a restaurant that can sign in but is not yet fully `activated`. */
export function isLimitedAccessStatus(status: string | null | undefined): boolean {
  return LIMITED_ACCESS_STATUSES.includes(status || '');
}

/** Default landing route for a published-but-not-activated restaurant. */
export const PUBLISHED_STATUS_DEFAULT_ROUTE = 'dashboard';

/** Sidebar items visible to a published-but-not-activated restaurant. Billing &
 * Subscription is visible but read-only — see SubscriptionComponent.canManagePlans. */
export const RESTAURANT_STATUS_MENU_TITLES: string[] = [
  'Home',
  'Menu',
  'Types & Categories',
  'Customizations',
  'Restaurant Profile',
  'Hardware',
  'Billing & Subscription',
  'Profile & Account',
  'Appearance',
  'Security',
  'Notifications',
];

/**
 * A `created` restaurant (not yet published) gets the published rules above
 * minus Billing & Subscription: there is no plan to look at before publishing.
 */
export const CREATED_STATUS_BLOCKED_ROUTES: string[] = ['billing-and-subscription'];
export const CREATED_STATUS_LOCKED_MENU_TITLES: string[] = ['Billing & Subscription'];

/** Whether a sidebar item is open to a limited-access (created/published) restaurant. */
export function isMenuTitleAllowedForStatus(title: string, status: string | null | undefined) {
  if (status === 'created' && CREATED_STATUS_LOCKED_MENU_TITLES.includes(title)) {
    return false;
  }
  return RESTAURANT_STATUS_MENU_TITLES.includes(title);
}

/** Whether a `/main` child route is open to a limited-access (created/published) restaurant. */
export function isRouteAllowedForStatus(path: string, status: string | null | undefined): boolean {
  const segment = (path || '').split('?')[0].split('/').filter(Boolean)[0] || '';
  if (status === 'created' && CREATED_STATUS_BLOCKED_ROUTES.includes(segment)) {
    return false;
  }
  return isRouteAllowedForPublishedRestaurant(path);
}

/**
 * Checks a `/main` child route against the published-status allow list.
 * `path` may carry route params (e.g. `update-menu/123`); only the first
 * segment is matched.
 */
export function isRouteAllowedForPublishedRestaurant(path: string): boolean {
  const segment = (path || '').split('?')[0].split('/').filter(Boolean)[0] || '';
  return PUBLISHED_STATUS_ROUTES.includes(segment);
}

/**
 * Header "Quick Add" guard keys hidden for a published-but-not-activated
 * restaurant. These are the quick-add shortcuts, not `/main` routes (some,
 * like `/add-table`, live outside `/main` entirely), so they're tracked
 * separately from PUBLISHED_STATUS_ROUTES rather than reusing its segment
 * matching.
 */
export const PUBLISHED_STATUS_HIDDEN_QUICK_ADD_GUARDS: string[] = [
  'table',
  'staffs',
  'station',
  'zone',
];
